import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import cns from 'classnames';
import { useLocation, useHistory } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { Search, X } from 'react-feather';
import { useCourtFormMasterSearchEnabled } from '~/src/entities/user/hooks/useFeatureAccess';
import CourtFormsSearchResults from './CourtFormsSearchResults';

type Props = {
  children: ReactNode;
  // Library opts into a sticky header and persisting the query to the URL; the
  // add-documents and questionnaire flows need neither.
  sticky?: boolean;
  syncToUrl?: boolean;
};

// Adds the master search bar above a court forms folder-navigation view,
// swapping in cross-region search results while a query is active.
function CourtFormsSearchable({
  children,
  sticky = false,
  syncToUrl = false,
}: Props) {
  const isMasterSearchEnabled = useCourtFormMasterSearchEnabled();
  const location = useLocation();
  const history = useHistory();

  const searchParam = syncToUrl
    ? new URLSearchParams(location.search).get('search') || ''
    : '';
  const [searchInput, setSearchInput] = useState(searchParam);
  const [debouncedQuery, setDebouncedQuery] = useState(searchParam);

  useEffect(() => {
    if (syncToUrl && !searchParam) {
      setSearchInput('');
      setDebouncedQuery('');
    }
  }, [syncToUrl, searchParam]);

  useDebounce(
    () => {
      setDebouncedQuery(searchInput);
      if (!syncToUrl) return;
      const params = new URLSearchParams(location.search);
      if ((params.get('search') || '') === searchInput) return;
      if (searchInput) params.set('search', searchInput);
      else params.delete('search');
      const queryString = params.toString();
      history.replace({ search: queryString ? `?${queryString}` : '' });
    },
    300,
    [searchInput],
  );

  if (!isMasterSearchEnabled) {
    return <>{children}</>;
  }

  const clearSearch = () => {
    setSearchInput('');
    setDebouncedQuery('');
    if (syncToUrl) history.replace({ search: '' });
  };

  const showSearch = debouncedQuery.length >= 3;

  return (
    <div>
      {/* Library renders this inside a scrolling list, so it opts into a sticky header. */}
      <div
        className={cns('mb-3', {
          'sticky -top-4 z-10 bg-white pt-4 pb-3 -mx-4 px-4 border-b border-gray-200':
            sticky,
        })}
      >
        <TextField
          value={searchInput}
          placeholder="Search all court forms..."
          fullWidth
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </div>

      {showSearch ? (
        <CourtFormsSearchResults query={debouncedQuery} />
      ) : (
        children
      )}
    </div>
  );
}

export default CourtFormsSearchable;
